import get from 'lodash/get';
import logger from '@greenlight/logger';
import { Analytics } from '../Analytics';
import { storageAvailable } from '../helpers';
import InvestService from '../services/invest/index.ts';
import { planPrices } from '../utils/prices';

const trackVerifyFundingSource = ({ eventProps }) =>
  Analytics.trackEvent('Funding Source Verified', {
    eventProps,
    delaySegmentTrackEvent: false
  });

const trackAddFundingSource = ({ eventProps }) =>
  Analytics.trackEvent('Funding Source Added', {
    eventProps,
    delaySegmentTrackEvent: false
  });

const mapper = (action, store) => {
  let checkPhoneError;

  let errorActions = [];
  switch (action.type) {
    case 'CHECK_PHONE_SUCCESS':
      if (
        action.payload.data &&
        action.payload.data.action === 'confirm-role'
      ) {
        Analytics.trackEvent('WebReg Flow Started');
      }
      break;
    case 'CHECK_PHONE_FAILURE':
      if (action.error.response) {
        checkPhoneError = action.error.response.data;
      }
      if (window.FS && typeof window.FS.event === `function`) {
        window.FS.event('Check Phone Failed', {
          error: checkPhoneError
        });
      }
      break;
    case 'LOGIN_SUCCESS':
      Analytics.loginUser(action.payload.data.user);
      break;

    case 'SAVE_DEBIT_SUCCESS':
    case 'SAVE_MANUAL_ACH_SUCCESS':
    case 'ANSWER_SUCCESS':
      if (
        !!action.payload.data.unresolvedErrors &&
        action.payload.data.unresolvedErrors.length > 0
      ) {
        errorActions = action.payload.data.unresolvedErrors.map(
          unresolvedError => unresolvedError.action
        );
        for (
          let i = 0;
          i < action.payload.data.unresolvedErrors.length;
          i += 1
        ) {
          const error = action.payload.data.unresolvedErrors[i];
          if (error.action === 'set_instant_funding_account') {
            logger.error(error.message);

            Analytics.trackEvent('Add Debit Card Failed', {
              eventProps: {
                error_code: error.code,
                error_message: error.message,
                during_registration: true
              }
            });
          }
        }
      }
      if (
        !!action.payload.data.extras &&
        action.payload.data.extras.length > 0
      ) {
        // eslint-disable-next-line no-restricted-syntax
        for (const extra of action.payload.data.extras) {
          if (
            extra.action === 'create_user' &&
            !errorActions.find(a => a === 'create_user')
          ) {
            Analytics.registerUser(
              extra.user,
              action.payload.data.flowId,
              store.getState().app.smsNotificationsOptedStatus
            );
          }
          if (
            extra.action === 'set_plaid_bank_account' &&
            !errorActions.find(a => a === 'set_plaid_bank_account')
          ) {
            const eventProps = {
              during_registration: true,
              funding_type: 'instant_ach'
            };
            trackAddFundingSource({ eventProps });
            trackVerifyFundingSource({ eventProps });
          }
          if (
            extra.action === 'set_instant_funding_account' &&
            !errorActions.find(a => a === 'set_instant_funding_account')
          ) {
            trackAddFundingSource({
              eventProps: {
                during_registration: true,
                funding_type: 'debit'
              }
            });

            const fundingInstrumentByType = {
              android_pay_card: 'googlePay',
              apple_pay_card: 'applePay'
            };
            const fundingType = get(extra, 'funding.type', '');

            Analytics.trackEvent('Debit Card Added', {
              eventProps: {
                during_registration: true,
                funding_type: 'debit',
                funding_instrument:
                  fundingInstrumentByType[fundingType] || 'card'
              }
            });

            if (!extra.needsVerify) {
              trackVerifyFundingSource({
                eventProps: {
                  during_registration: true,
                  funding_type: 'debit',
                  funding_instrument:
                    fundingInstrumentByType[fundingType] || 'card',
                  role: 'parent'
                }
              });
            }
          }
          if (
            extra.action === 'set_manual_bank_account' &&
            !errorActions.find(a => a === 'set_manual_bank_account')
          ) {
            trackAddFundingSource({
              eventProps: {
                during_registration: true,
                funding_type: 'manual_ach'
              }
            });
          }
        }
      }

      if (store.getState().app.planChangesTermsAndConditions) {
        if (
          action.payload.data.extras &&
          action.payload.data?.extras[0]?.action === 'verify_identity' &&
          get(store.getState(), 'app.answers.setPlan.pricingPlanName') &&
          store.getState().app.answers.setPlan.pricingPlanName !== 'standard'
        ) {
          Analytics.trackEvent('T&C Accepted', {
            eventProps: {
              during_registration: true,
              gl_product: 'Invest'
            }
          });

          InvestService.postAcceptTermsAndConditions({
            deviceId: store.getState().app.deviceToken,
            authToken: store.getState().app.stargatewayKey
          });
        }
      }

      break;

    case 'SAVE_PLAID_ACCOUNT_SUCCESS':
      trackAddFundingSource({
        eventProps: {
          during_registration: true,
          funding_type: 'instant_ach'
        }
      });

      trackVerifyFundingSource({
        eventProps: {
          during_registration: true,
          role: action.payload.data.user.role,
          funding_type: 'instant_ach'
        }
      });
      break;

    case 'SAVE_ACH_ACCOUNT_SUCCESS':
      trackAddFundingSource({
        eventProps: {
          during_registration: true,
          funding_type: 'manual_ach'
        }
      });
      break;

    case 'COMPLETE_SUCCESS':
      Analytics.registrationComplete(action.payload.data.extras[0].user, planPrices(store.getState().featureToggles));
      Analytics.updateFBIdentity(action.payload.data.extras[0].user);
      if (storageAvailable('sessionStorage')) {
        sessionStorage.setItem('userCompleted', true);
      }
      break;

    case 'VERIFY_DEBIT_NOW_SUCCESS':
      Analytics.trackBankAccountVerifiedEvent('debit');
      trackVerifyFundingSource({
        eventProps: {
          during_registration: true,
          funding_type: 'debit'
        }
      });
      break;

    case 'VERIFY_DEBIT_NOW_FAILURE':
      let maxAttempts = false;
      if (
        action.error.response &&
        action.error.response.data &&
        action.error.response.data.code ===
          'ERR_MAX_CHALLENGE_WITHDRAWAL_VERIFICATIONS'
      ) {
        maxAttempts = true;
      }
      Analytics.trackEvent('Verify Debit Card Failed', {
        eventProps: {
          maxAttempts,
          during_registration: true,
          // debit_failed_verifications in an incrementing property. When this event is tracked it will increase by 1.
          debit_failed_verifications: 1
        }
      });

      break;

    case 'SAVE_DEBIT_FAILURE':
      if (
        action.error.response &&
        action.error.response.data &&
        Array.isArray(action.error.response.data.message)
      ) {
        logger.error(action.error.response.data.message);
      }
      Analytics.trackEvent('Add Debit Card Failed', {
        eventProps: {
          error_code: action.error.response
            ? action.error.response.data.code
            : '',
          error_message: action.error.response
            ? action.error.response.data.message
            : '',
          during_registration: true
        }
      });
      break;

    case 'ANSWER_FAILURE':
      if (
        action.error.response &&
        action.error.response.data &&
        Array.isArray(action.error.response.data.message)
      ) {
        logger.error(action.error.response.data.message);
      }
      break;

    case 'INITIALIZE_SUCCESS':
      Analytics.identifyUser({ flowId: action.payload.data.flowId });
      break;

    default:
      break;
  }
};

const AnalyticsMiddleware = store => next => action => {
  mapper(action, store);
  next(action);
};
export default AnalyticsMiddleware;
